import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { useMemo, useId, useEffect as useEffect$1, useContext as useContext$1 } from 'react';
import { StyleSheet, css } from 'aphrodite';

function flatten(list) {
  const result = [];
  if (!list) {
    return result;
  } else if (Array.isArray(list)) {
    for (const item of list) {
      result.push(...flatten(item));
    }
  } else {
    result.push(list);
  }
  return result;
}
function processStyleList(style) {
  const stylesheetStyles = [];
  const inlineStyles = [];
  if (!style) {
    return {
      style: {},
      className: ""
    };
  }
  const shouldInlineStyles = typeof global !== "undefined" && global.SNAPSHOT_INLINE_APHRODITE;
  flatten(style).forEach(child => {
    const _definition = child._definition;
    if (_definition != null) {
      if (shouldInlineStyles) {
        const def = {};
        for (const [key, value] of Object.entries(_definition)) {
          def[key.replace(/-[a-z]/g, match => match[1].toUpperCase())] = value;
        }
        inlineStyles.push(def);
      } else {
        stylesheetStyles.push(child);
      }
    } else {
      inlineStyles.push(child);
    }
  });
  const inlineStylesObject = Object.assign({}, ...inlineStyles);
  if (inlineStyles.length > 0 && !shouldInlineStyles) {
    const inlineStylesStyleSheet = StyleSheet.create({
      inlineStyles: inlineStylesObject
    });
    stylesheetStyles.push(inlineStylesStyleSheet.inlineStyles);
  }
  return {
    style: shouldInlineStyles ? inlineStylesObject : {},
    className: css(...stylesheetStyles)
  };
}

const _excluded$2 = ["children", "style", "tag", "testId"];
const isHeaderRegex = /^h[1-6]$/;
const styles$1 = StyleSheet.create({
  text: {
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale"
  },
  header: {
    marginTop: 0,
    marginBottom: 0
  }
});
const Text = React.forwardRef(function Text(_ref, ref) {
  let {
      children,
      style,
      tag: Tag = "span",
      testId
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  const isHeader = isHeaderRegex.test(Tag);
  const styleAttributes = processStyleList([styles$1.text, isHeader && styles$1.header, style]);
  const classNames = otherProps.className ? [otherProps.className, styleAttributes.className].join(" ") : styleAttributes.className;
  return (React.createElement(Tag, _extends({}, otherProps, {
      style: styleAttributes.style,
      className: classNames,
      "data-testid": testId,
      ref: ref
    }), children)
  );
});

const _excluded$1 = ["className", "style"];
function addStyle(Component, defaultStyle) {
  return React.forwardRef((props, ref) => {
    const _ref = props,
      {
        className,
        style
      } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, _excluded$1);
    const reset = typeof Component === "string" ? overrides[Component] : null;
    const {
      className: aphroditeClassName,
      style: inlineStyles
    } = processStyleList([reset, defaultStyle, style]);
    return React.createElement(Component, _extends({}, otherProps, {
      ref: ref,
      className: [aphroditeClassName, className].filter(Boolean).join(" "),
      style: inlineStyles
    }));
  });
}
const overrides = StyleSheet.create({
  button: {
    margin: 0,
    "::-moz-focus-inner": {
      border: 0
    }
  }
});

const _excluded = ["testId", "tag"];
const styles = StyleSheet.create({
  default: {
    alignItems: "stretch",
    borderWidth: 0,
    borderStyle: "solid",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: 0,
    position: "relative",
    zIndex: 0,
    minHeight: 0,
    minWidth: 0
  }
});
const View = React.forwardRef(function View(props, ref) {
  const {
      testId,
      tag = "div"
    } = props,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  const commonProps = _extends({}, restProps, {
    "data-testid": testId
  });
  const StyledTag = useMemo(() => addStyle(tag, styles.default), [tag]);
  return React.createElement(StyledTag, _extends({}, commonProps, {
    ref: ref
  }));
});

let RenderState = function (RenderState) {
  RenderState["Initial"] = "initial";
  RenderState["Standard"] = "standard";
  return RenderState;
}({});
let RenderStateInternal = function (RenderStateInternal) {
  RenderStateInternal["Root"] = "root";
  RenderStateInternal["Initial"] = "initial";
  RenderStateInternal["Standard"] = "standard";
  return RenderStateInternal;
}({});
const RenderStateContext = React.createContext(RenderStateInternal.Root);
RenderStateContext.displayName = "RenderStateContext";

class InitialFallback extends React.Component {
  constructor(...args) {
    super(...args);
    this.state = {
      mounted: false
    };
    this._isTheRootComponent = false;
  }
  componentDidMount() {
    if (this._isTheRootComponent) {
      this.setState({
        mounted: true
      });
    }
  }
  _renderAsRootComponent() {
    const {
      mounted
    } = this.state;
    const {
      children,
      fallback
    } = this.props;
    this._isTheRootComponent = true;
    if (mounted) {
      return React.createElement(RenderStateContext.Provider, {
        value: RenderStateInternal.Standard
      }, children());
    }
    if (fallback) {
      return React.createElement(RenderStateContext.Provider, {
        value: RenderStateInternal.Initial
      }, fallback());
    }
    return null;
  }
  _maybeRender(renderState) {
    const {
      children,
      fallback
    } = this.props;
    switch (renderState) {
      case RenderStateInternal.Root:
        return this._renderAsRootComponent();
      case RenderStateInternal.Initial:
        return fallback ? fallback() : null;
      case RenderStateInternal.Standard:
        return children();
    }
    {
      var _JSON$stringify;
      console.log(`We got a render state we don't understand: "${(_JSON$stringify = JSON.stringify(renderState)) != null ? _JSON$stringify : ""}"`);
      return this._maybeRender(RenderStateInternal.Root);
    }
  }
  render() {
    return React.createElement(RenderStateContext.Consumer, null, value => this._maybeRender(value));
  }
}

let serverSide = false;
var Server = {
  isServerSide: () => serverSide,
  setServerSide: () => {
    serverSide = true;
  }
};

const Id = ({
  id,
  children
}) => {
  const generatedId = useId();
  return React.createElement(React.Fragment, null, children(id != null ? id : generatedId));
};

const useForceUpdate = () => {
  const [, setUpdateState] = React.useState({});
  const forceUpdate = React.useCallback(() => {
    setUpdateState({});
  }, []);
  return forceUpdate;
};

const useIsMounted = () => {
  const isMountedRef = React.useRef(false);
  const isMounted = React.useCallback(() => isMountedRef.current, []);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return isMounted;
};

function useLatestRef(value) {
  const ref = React.useRef(value);
  ref.current = value;
  return ref;
}

const useOnMountEffect = callback => {
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    const cleanup = callback(isMountedRef);
    return () => {
      cleanup == null || cleanup();
      isMountedRef.current = false;
    };
  }, []);
};

const useOnline = () => {
  const forceUpdate = useForceUpdate();
  useEffect$1(() => {
    const handleChange = () => forceUpdate();
    window.addEventListener("online", handleChange);
    window.addEventListener("offline", handleChange);
    return () => {
      window.removeEventListener("online", handleChange);
      window.removeEventListener("offline", handleChange);
    };
  }, [forceUpdate]);
  return navigator.onLine;
};

const usePreHydrationEffect = effect => {
  const effectCallRef = React.useRef(Server.isServerSide() ? () => {} : React.useLayoutEffect);
  effectCallRef.current(effect, []);
};

const useRenderState = () => {
  const rawRenderState = useContext$1(RenderStateContext);
  if (rawRenderState === RenderStateInternal.Standard) {
    return RenderState.Standard;
  } else {
    return RenderState.Initial;
  }
};

const {
  useEffect,
  useState,
  useContext
} = React;
const RenderStateRoot = ({
  children,
  throwIfNested: _throwIfNested = true
}) => {
  const [firstRender, setFirstRender] = useState(true);
  const renderState = useContext(RenderStateContext);
  useEffect(() => {
    setFirstRender(false);
  }, []);
  if (renderState !== RenderStateInternal.Root) {
    if (_throwIfNested) {
      throw new Error("There's already a <RenderStateRoot> above this instance in " + "the render tree.  This instance should be removed.");
    }
    return React.createElement(React.Fragment, null, children);
  }
  const value = firstRender ? RenderStateInternal.Initial : RenderStateInternal.Standard;
  return React.createElement(RenderStateContext.Provider, {
    value: value
  }, children);
};

const keys = {
  enter: "Enter",
  escape: "Escape",
  tab: "Tab",
  space: " ",
  up: "ArrowUp",
  down: "ArrowDown"
};

export { Id, InitialFallback, RenderState, RenderStateRoot, Server, Text, View, addStyle, keys, useForceUpdate, useIsMounted, useLatestRef, useOnMountEffect, useOnline, usePreHydrationEffect, useRenderState };
